import React from 'react';
import { Link } from 'react-router-dom';
import {
  Icon,
  Button,
  P,
  FormInputText,
} from '@fashionnovateam/nova-ui-toolkit';
import validateEmail from '../../Lib/Utils/Helpers/validate-email';

export type EmailFormProps = {
  isInfluencer: boolean;
  resetPassword: (email: string) => Promise<void>;
  onError: (error: string | null) => void;
};

export function EmailForm(props: EmailFormProps) {
  const { isInfluencer, resetPassword, onError } = props;

  const [loading, setLoading] = React.useState(false);
  const [errors, setErrors] = React.useState<{ email?: string }>({});
  const [emailSent, setEmailSent] = React.useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onError(null);

    const form = event.currentTarget as HTMLFormElement;
    const email = form.email.value;
    if (!validateEmail(email)) {
      setErrors({ email: 'Please enter a valid email address.' });
      return;
    }

    try {
      setErrors({});
      setLoading(true);
      await resetPassword(email);
      setEmailSent(true);
    } catch (err: unknown) {
      onError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailBlur = (email: string) => {
    setErrors((prev) => ({
      ...prev,
      email: validateEmail(email)
        ? undefined
        : 'Please enter a valid email address.',
    }));
  };

  if (emailSent) {
    return (
      <div className="flex flex-col gap-10">
        <div className="flex flex-col gap-2">
          <P size="xl" weight="medium">
            Password reset email sent
          </P>
          <P size="sm" color="disabled">
            If you have an account with us, we&apos;ve sent you an email to
            reset your password.
          </P>
        </div>
        <Link to={isInfluencer ? '/influencer-login' : '/login'}>
          <Button className="w-full" color="secondary">
            Back to Sign In
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <form className="flex flex-col gap-10" onSubmit={handleSubmit} noValidate>
      <div className="flex flex-col gap-4">
        <P size="xl" weight="medium">
          Reset your password
        </P>
        <FormInputText
          label="Email"
          name="email"
          inputId="email"
          type="email"
          disabled={loading}
          error={errors.email}
          onBlurValue={handleEmailBlur}
        />
      </div>
      <div className="flex flex-col gap-6">
        <Button className="w-full" type="submit" loading={loading}>
          Continue
          <Icon.ArrowRight />
        </Button>
        <Link
          to={isInfluencer ? '/influencer-login' : '/login'}
          className="w-full flex justify-center text-xs font-medium text-primary-700 hover:text-primary-900"
        >
          Back to Sign In
        </Link>
      </div>
    </form>
  );
}
